import { Component } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { appRef } from '../../app'
import { HiWifi, HiStatusOnline, HiOutlineXCircle, HiOutlineDeviceMobile } from 'react-icons/hi'
import { FaEthernet } from "react-icons/fa";
import { ClientModel, getClients, IClientData } from '../../api/clients'
import { toast } from 'react-toastify'
import Loader from '../../components/loader'
import { onCustomerConnect, clearCustomerConnect, IListenerUID, CustomerState } from '../../wsclient/handlers/customertraffic.handler'
import { getString, getLocale } from '../../locale'
import TimeAgo from 'react-timeago'
// @ts-ignore
import heStrings from 'react-timeago/lib/language-strings/he'
// @ts-ignore
import buildFormatter from 'react-timeago/lib/formatters/buildFormatter'
import classNames from 'classnames'
import { IsDevIpAddress } from '../../config';

const formatter = buildFormatter(heStrings)

type Props = RouteComponentProps & {
    external?: boolean
}

type State = {
    clients?: IClientData[]
    multiSelect?: boolean
    selectedClients?: IClientData[]
}

export class Dashboard extends Component<Props, State> {
    state: State = {}

    listenerId?: IListenerUID
    fallbackPollTimer?: ReturnType<typeof setInterval>
    reconcileTimeout?: ReturnType<typeof setTimeout>
    isRefreshingClients = false
    lastWsRevision?: number

    componentDidMount = async () => {
        appRef.setHeader(<HiWifi className="me-2" size={28} />, () => this.props.external ? getString('pagelabels.dashboard_external') : getString('pagelabels.dashboard_internal'))
        await this.refreshClients(true)
        this.listenerId = onCustomerConnect(this.handleClientConnect.bind(this))
        // Safety-net only; primary sync is websocket + revision gap reconciliation.
        this.fallbackPollTimer = setInterval(() => {
            this.refreshClients(false)
        }, 60000)
        window.addEventListener('focus', this.handleWindowFocus)
        document.addEventListener('visibilitychange', this.handleVisibilityChange)
    }

    componentWillUnmount = () => {
        clearCustomerConnect(this.listenerId!)
        if (this.fallbackPollTimer) clearInterval(this.fallbackPollTimer)
        if (this.reconcileTimeout) clearTimeout(this.reconcileTimeout)
        window.removeEventListener('focus', this.handleWindowFocus)
        document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    }

    async refreshClients(showErrorToast: boolean) {
        if (this.isRefreshingClients) return
        this.isRefreshingClients = true
        try {
            const clients = (await getClients()).data
            this.setState({ clients })
        } catch (err) {
            if (showErrorToast) {
                toast.error((err as Error).message)
            }
            console.error(err)
        } finally {
            this.isRefreshingClients = false
        }
    }

    scheduleReconcile() {
        if (this.reconcileTimeout) clearTimeout(this.reconcileTimeout)
        this.reconcileTimeout = setTimeout(() => {
            this.refreshClients(false)
        }, 250)
    }

    handleClientConnect(client: IClientData, state: CustomerState, revision?: number) {
        if (typeof revision === 'number' && Number.isFinite(revision) && revision > 0) {
            if (this.lastWsRevision !== undefined && revision > this.lastWsRevision + 1) {
                // Gap detected (dropped/out-of-order event). Reconcile immediately.
                this.scheduleReconcile()
            }
            this.lastWsRevision = Math.max(this.lastWsRevision ?? 0, revision)
        } else {
            // Legacy/no-revision event: reconcile once.
            this.scheduleReconcile()
        }

        if (state === CustomerState.Connected) {
            // Upsert by ID to avoid duplicate cards on repeated Connected broadcasts.
            this.setState(prevState => ({
                clients: (prevState.clients || []).filter(c => c.id !== client.id).concat(client),
            }))
        }
        if (state === CustomerState.Disconnected) {
            this.setState(prevState => ({
                clients: (prevState.clients || []).filter(c => c.id !== client.id),
            }))
        }
    }

    handleWindowFocus = () => {
        this.refreshClients(false)
    }

    handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            this.refreshClients(false)
        }
    }

    async handleClientClick(client: IClientData) {
        this.props.history.push(`/client/${client.id}`)
    }

    async handleClientContext(client: IClientData) {
        const { multiSelect } = this.state

        if (!multiSelect) {
            // const isEnter = await askYesNo(getString('dashboard.confirm_client_click'), {
            //     confirmButtonText: getString('dashboard.enter_client'),
            //     cancelButtonText: getString('dashboard.multiple_clients'),
            //     cancelButtonColor: 'blue'
            // });
            // if (isEnter) {
            //     this.props.history.push(`/client/${client.id}`)
            // } else {
            //     this.setState({ multiSelect: true, selectedClients: [client] })
            // }
            this.setState({ multiSelect: true, selectedClients: [client] })
        } else {
            const { selectedClients } = this.state
            const index = selectedClients!.findIndex(c => c.id === client.id)
            if (index === -1) {
                this.setState({ selectedClients: selectedClients!.concat(client) })
            } else {
                this.setState({ selectedClients: selectedClients!.filter(c => c.id !== client.id) })
            }
        }
    }

    render() {
        return <div className="flex flex-col items-center">
            {this.renderMultiClientActions()}
            {this.renderClients()}
        </div>
    }

    renderMultiClientActions() {
        const { multiSelect, selectedClients } = this.state
        if (!multiSelect) return null

        return (
            <div className="mb-10 flex flex-row items-center justify-between w-full mt-4 text-white">
                <div>
                    {getString('dashboard.selected_clients')}: {selectedClients!.length}
                </div>
                <div>
                    <button
                        className="cursor-pointer mx-4 group py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-501 duration-200 bg-red-600 hover:bg-red-700"
                        onClick={() => this.setState({ multiSelect: false, selectedClients: undefined })}
                    >
                        {getString('common.cancel')}
                    </button>
                    <button
                        className="cursor-pointer mx-4 group py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-501 duration-200 bg-blue-600 hover:bg-blue-700"
                        onClick={() => {
                            const { selectedClients } = this.state
                            // if (selectedClients!.length === 1) {
                            // this.props.history.push(`/client/${selectedClients![0].id}`)
                            // } else {
                            this.props.history.push(`/multiclient/${selectedClients!.map(c => c.id).join(',')}`)
                            // }
                        }}
                    >
                        {getString('dashboard.view_clients')}
                    </button>
                </div>
            </div>
        )
    }

    renderClients() {
        const { clients } = this.state
        if (clients === undefined) return <Loader size={12} />
        if (clients.length === 0)
            return (
                <div className="w-full h-full dark:text-white flex flex-row items-center justify-center flex-wrap">
                    <HiOutlineXCircle className="me-1" />
                    <h1>{getString('dashboard.no_clients')}</h1>
                </div>
            )
        return <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 w-full">{clients.map(this.renderClient.bind(this))}</div>
    }

    renderClient(client: IClientData, i: number) {
        const { external } = this.props

        const isDevIp = IsDevIpAddress(client.remoteAddress)
        if (external && isDevIp) return null
        if (!external && !isDevIp) return null

        const { multiSelect, selectedClients } = this.state

        const hasName = !!(client.machine && client.machine.name)
        const isSelected = multiSelect && selectedClients!.findIndex(c => c.id === client.id) !== -1
        return (
            <div
                key={i}
                className={classNames(
                    'max-w-md mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden md:max-w-2xl md:hover-grow',
                    client.phone !== undefined && 'col-span-2',
                    isSelected && 'border-2 dark:border-white border-blue-500'
                )}
                onClick={() => this.handleClientClick(client)}
                onContextMenu={(e) => {
                    e.preventDefault()
                    this.handleClientContext(client)
                }}
            >
                <div className="md:flex md:items-center md:content-center md:ms-3">
                    <div className="md:flex-shrink-0">
                        {client.phone === undefined ? (
                            client.phyDebugger ? (
                                <FaEthernet className="h-24 w-full object-cover md:w-24 dark:text-gray-400" />
                            ) : (
                                <HiStatusOnline className="h-24 w-full object-cover md:w-24 dark:text-gray-400" />
                            )
                        ) : (
                            <HiOutlineDeviceMobile className="h-24 w-full object-cover md:w-24 dark:text-gray-400" />
                        )}
                    </div>
                    <div className="p-4 md:py-6 flex-1">
                        <div className={classNames("tracking-wide text-2xl text-indigo-500 font-semibold", !hasName && 'uppercase')}>
                            {hasName ? client.machine!.name : client.id}
                        </div>
                        {client.phone !== undefined ? this.renderPhoneClientDetails(client, hasName) : this.renderRegularClientDetails(client, hasName)}
                    </div>
                </div>
            </div>
        )
    }

    renderRegularClientDetails(client: IClientData, hasName: boolean) {
        return (
            <div className="mt-2">
                {hasName && this.renderInfoRow(getString('dashboard.client_id'), client.id, true)}
                {this.renderInfoRow(getString('dashboard.ip'), client.remoteAddress.replace('::ffff:', ''), true)}
                {this.renderInfoRow(getString('dashboard.model'), ClientModel[client.model])}
                <div className="mt-2 text-sm dark:text-white">
                    {getString('dashboard.connected')}{' '}
                    <TimeAgo date={client.createdAt} formatter={getLocale() === 'he' ? formatter : undefined} />
                </div>
            </div>
        )
    }

    renderPhoneClientDetails(client: IClientData, hasName: boolean) {
        return (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                    {this.renderInfoRow(getString('dashboard.device_name'), client.phone?.deviceName, true)}
                    {this.renderInfoRow(getString('dashboard.phone_model'), client.phone?.model)}
                    {this.renderInfoRow(getString('dashboard.system_name'), client.phone?.systemName, true)}
                    {this.renderInfoRow(getString('dashboard.system_version'), client.phone?.systemVersion, true)}
                </div>
                <div>
                    {hasName && this.renderInfoRow(getString('dashboard.client_id'), client.id, true)}
                    {this.renderInfoRow(getString('dashboard.ip'), client.remoteAddress.replace('::ffff:', ''), true)}
                    {this.renderInfoRow(getString('dashboard.model'), ClientModel[client.model])}
                    <div className="mt-3 text-sm dark:text-white">
                        {getString('dashboard.connected')}{' '}
                        <TimeAgo date={client.createdAt} formatter={getLocale() === 'he' ? formatter : undefined} />
                    </div>
                </div>
            </div>
        )
    }

    renderInfoRow(label: string, value?: string, ltr = false) {
        return (
            <div className="flex items-start text-sm text-gray-600 dark:text-gray-300 mb-1">
                <div className="font-medium me-2 whitespace-nowrap">{label}:</div>
                <div className={classNames('min-w-0 break-words', ltr && 'ltr')}>{value || '-'}</div>
            </div>
        )
    }
}
